import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, apiErrorMessage } from '@/lib/api'
import { useState } from 'react'
import { useAuth } from '@/stores/authStore'

type ListingDetail = {
  id: string
  skillId: string
  publisherId: string
  description: string
  forkCount: number
  ratingAvg: number
  ratingCount: number
  publishedAt: string
  skill: {
    id: string
    name: string
    description: string
    domain: string
    riskLevel: string
    industry: string | null
    category: string | null
    tags: string[]
    language: string
    targetModels: string[]
    complianceFrameworks: string[]
    version: string
    certifiedAt: string | null
    inputSchema: Record<string, unknown>
    outputSchema: Record<string, unknown>
    scope: string[]
    outOfScope: string[]
    guardrails: Array<{ id: string; type: string; description: string; validated: boolean }>
  }
  publisher: { id: string; name: string; publisherVerified: boolean; verificationLabel: string | null }
  reviews: Array<{ id: string; rating: number; comment: string | null; createdAt: string; user: { id: string; name: string } }>
}

export default function MarketplaceDetailPage() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const qc = useQueryClient()
  const [rating, setRating] = useState(5)
  const [comment, setComment] = useState('')

  const query = useQuery<ListingDetail>({
    queryKey: ['marketplace', id],
    queryFn: async () =>
      (await apiClient.get<ListingDetail>(`/api/v1/marketplace/${id}`)).data,
    enabled: Boolean(id),
  })

  const forkMut = useMutation({
    mutationFn: async () => {
      return (await apiClient.post(`/api/v1/marketplace/${id}/fork`)).data as { id: string }
    },
    onSuccess: (data) => {
      window.location.assign(`/skills/${data.id}`)
    },
  })

  const reviewMut = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/api/v1/marketplace/${id}/review`, { rating, comment })
    },
    onSuccess: () => {
      setComment('')
      qc.invalidateQueries({ queryKey: ['marketplace', id] })
    },
  })

  if (query.isLoading) return <p className="text-sm text-neutral-500">Loading…</p>
  if (query.error || !query.data)
    return <p className="text-sm text-red-600">Failed to load listing</p>

  const d = query.data
  const s = d.skill
  const isOwnListing = user && d.publisherId === user.id

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs text-neutral-500 flex gap-2">
          <span>v{s.version}</span>
          <span>·</span>
          <span>{s.domain}</span>
          <span>·</span>
          <span>risk {s.riskLevel}</span>
          <span>·</span>
          <span>published {new Date(d.publishedAt).toLocaleDateString()}</span>
        </div>
        <h1 className="text-2xl font-semibold">{s.name}</h1>
        <p className="mt-1 text-sm text-neutral-600">{d.description}</p>
        <p className="mt-2 text-xs text-neutral-500">
          By {d.publisher.name}
          {d.publisher.publisherVerified && (
            <span className="ml-1 text-green-700">✓ verified</span>
          )}
          · {d.forkCount} forks · {d.ratingCount > 0 ? `${d.ratingAvg.toFixed(1)}★ (${d.ratingCount})` : 'no ratings'}
        </p>
      </div>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-2">About this skill</h2>
        <p className="text-sm text-neutral-700">{s.description}</p>
      </section>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-2">Scope</h2>
        <ul className="text-sm list-disc pl-5">{s.scope.map((x) => <li key={x}>{x}</li>)}</ul>
        <h2 className="font-semibold mt-3 mb-2">Out of scope</h2>
        <ul className="text-sm list-disc pl-5">{s.outOfScope.map((x) => <li key={x}>{x}</li>)}</ul>
      </section>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-2">Guardrails ({s.guardrails.length})</h2>
        <ul className="space-y-1 text-sm">
          {s.guardrails.map((g) => (
            <li key={g.id}>
              <span className="font-medium">{g.type}:</span> {g.description}
            </li>
          ))}
        </ul>
      </section>

      {user && !isOwnListing && (
        <section className="rounded border bg-white p-4 space-y-3">
          <h2 className="font-semibold">Actions</h2>
          <button
            onClick={() => forkMut.mutate()}
            disabled={forkMut.isPending}
            className="rounded bg-neutral-900 text-white text-sm px-3 py-1.5 disabled:opacity-50"
          >
            {forkMut.isPending ? 'Forking…' : 'Fork into my workspace'}
          </button>
          {forkMut.error && (
            <p className="text-xs text-red-600">{apiErrorMessage(forkMut.error)}</p>
          )}

          <form
            className="flex gap-2 items-center"
            onSubmit={(e) => {
              e.preventDefault()
              reviewMut.mutate()
            }}
          >
            <select
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
              className="rounded border px-2 py-1 text-sm"
            >
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={n} value={n}>{n} ★</option>
              ))}
            </select>
            <input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional comment"
              className="flex-1 rounded border px-2 py-1 text-sm"
            />
            <button
              type="submit"
              disabled={reviewMut.isPending}
              className="rounded border text-sm px-3 py-1 disabled:opacity-50"
            >
              {reviewMut.isPending ? 'Saving…' : 'Submit review'}
            </button>
          </form>
          {reviewMut.error && (
            <p className="text-xs text-red-600">{apiErrorMessage(reviewMut.error)}</p>
          )}
        </section>
      )}

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-3">Reviews ({d.reviews.length})</h2>
        {d.reviews.length === 0 ? (
          <p className="text-sm text-neutral-500">No reviews yet.</p>
        ) : (
          <ul className="divide-y">
            {d.reviews.map((r) => (
              <li key={r.id} className="py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{r.user.name}</span>
                  <span className="text-xs text-neutral-500">
                    {'★'.repeat(r.rating)}
                    {'☆'.repeat(5 - r.rating)}
                  </span>
                </div>
                {r.comment && <p className="mt-1 text-neutral-700">{r.comment}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
